import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Loader2, MapPin } from 'lucide-react'
import { Keyboard } from '@capacitor/keyboard'
import { Capacitor } from '@capacitor/core'
import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { getPlatform, isNative } from '@/lib/platform'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { db } from '@/lib/firebase'
import { useAuthState } from '@/hooks/useAuth'

const CITY_REQUESTS_COLLECTION = 'cityRequests'

interface RequestCityDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type SubmitPhase = 'idle' | 'submitting' | 'success'

export function RequestCityDialog({ open, onOpenChange }: RequestCityDialogProps) {
  const { user, loading: authLoading } = useAuthState()
  const [mounted, setMounted] = useState(false)
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const [draft, setDraft] = useState('')
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>('idle')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const successCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!open) {
      setDraft('')
      setSubmitPhase('idle')
      setValidationError(null)
      setSaveError(null)
      if (successCloseRef.current) {
        clearTimeout(successCloseRef.current)
        successCloseRef.current = null
      }
    }
  }, [open])

  useEffect(() => {
    return () => {
      if (successCloseRef.current) clearTimeout(successCloseRef.current)
    }
  }, [])

  useEffect(() => {
    const platform = getPlatform()
    if (platform !== 'ios') return

    let showListener: { remove: () => Promise<void> } | undefined
    let hideListener: { remove: () => Promise<void> } | undefined

    const setup = async () => {
      showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
        setKeyboardHeight(info.keyboardHeight)
      })
      hideListener = await Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardHeight(0)
      })
    }

    void setup()

    return () => {
      void showListener?.remove()
      void hideListener?.remove()
    }
  }, [])

  const dismissKeyboard = useCallback(() => {
    inputRef.current?.blur()
    if (Capacitor.isNativePlatform()) {
      void Keyboard.hide().catch(() => {})
    }
  }, [])

  const getModalTop = () => {
    if (keyboardHeight > 0) {
      return `calc((100dvh - ${keyboardHeight}px) / 2)`
    }
    return isNative() ? 'calc(100dvh / 2)' : '50%'
  }

  const handleOverlayPointerDown = () => {
    if (submitPhase !== 'idle') return
    dismissKeyboard()
    onOpenChange(false)
  }

  const handleSubmit = async () => {
    setSaveError(null)
    setValidationError(null)
    if (submitPhase !== 'idle') return
    if (authLoading) return

    const text = draft.trim()
    if (!text) {
      setValidationError('Please enter a city name')
      return
    }
    if (!user) {
      setSaveError('Sign in to request a city')
      return
    }

    setSubmitPhase('submitting')
    try {
      await addDoc(collection(db, CITY_REQUESTS_COLLECTION), {
        city: text,
        userId: user.uid,
        createdAt: serverTimestamp(),
      })
      dismissKeyboard()
      setDraft('')
      setSubmitPhase('success')
      if (successCloseRef.current) clearTimeout(successCloseRef.current)
      successCloseRef.current = setTimeout(() => {
        successCloseRef.current = null
        setSubmitPhase('idle')
        onOpenChange(false)
      }, 1500)
    } catch (err) {
      console.error('[RequestCityDialog] Failed to save request:', err)
      setSaveError('Could not send your request. Try again.')
      setSubmitPhase('idle')
    }
  }

  if (!mounted || !open) return null

  const inputDisabled = submitPhase !== 'idle' || authLoading
  const buttonDisabled = submitPhase === 'submitting' || submitPhase === 'success' || authLoading

  return (
    <>
      <div
        role="presentation"
        className="fixed inset-0 z-[55] bg-black/50 animate-in fade-in-0"
        onClick={handleOverlayPointerDown}
      />

      <div
        className="fixed left-0 right-0 z-[60] flex max-h-[90dvh] items-center justify-center pointer-events-none px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-city-title"
        aria-describedby="request-city-description"
        style={{
          top: getModalTop(),
          transform: 'translateY(-50%)',
          transition: 'top 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}
      >
        <div className="relative w-full max-w-sm overflow-y-auto rounded-[var(--card-radius)] bg-background shadow-lg animate-in zoom-in-95 pointer-events-auto">
          <div className="space-y-4 p-6">
            <div className="space-y-1.5">
              <h2 id="request-city-title" className="text-xl font-semibold tracking-tight text-foreground">
                Request a City
              </h2>
              <p id="request-city-description" className="text-sm leading-snug text-muted-foreground">
                Leave a request where you would like to see GetMoreTats next!
              </p>
            </div>

            <Input
              ref={inputRef}
              value={draft}
              disabled={inputDisabled}
              onChange={(e) => {
                setDraft(e.target.value)
                setValidationError(null)
                setSaveError(null)
              }}
              placeholder="City name"
              enterKeyHint="done"
              autoComplete="address-level2"
              className={cn(
                'h-12 rounded-xl text-base transition-[opacity,background-color,border-color,color]',
                'border-border bg-background disabled:opacity-50',
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (submitPhase !== 'idle') return
                  if (draft.trim()) {
                    void handleSubmit()
                  } else {
                    dismissKeyboard()
                  }
                }
              }}
            />

            {validationError ? (
              <p className="text-sm text-destructive" role="alert">
                {validationError}
              </p>
            ) : null}
            {saveError ? (
              <p className="text-sm text-destructive" role="alert">
                {saveError}
              </p>
            ) : null}

            <Button
              type="button"
              variant="default"
              className={cn(
                'h-12 w-full gap-2 rounded-xl text-base font-medium transition-colors duration-300',
                submitPhase === 'success' &&
                  'border-0 bg-emerald-600 text-white hover:bg-emerald-600 focus-visible:ring-emerald-500 disabled:opacity-100',
              )}
              disabled={buttonDisabled}
              onClick={() => void handleSubmit()}
            >
              {submitPhase === 'submitting' ? (
                <>
                  <Loader2 className="h-5 w-5 shrink-0 animate-spin" aria-hidden />
                  <span>Sending…</span>
                </>
              ) : submitPhase === 'success' ? (
                <>
                  <Check className="h-5 w-5 shrink-0" strokeWidth={2.5} aria-hidden />
                  <span>Thanks!</span>
                </>
              ) : (
                <>
                  <MapPin className="h-4 w-4 shrink-0" aria-hidden />
                  <span>Request!</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
